import { useMemo, useState, type ReactNode } from 'react'
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react'

import { useApAging, useArAging, useRevenueDetail } from './api'
import { colors, fmt } from './utils'
import type { ApAgingRow, ArAgingRow, RevenueDetailRow } from './types'

const REVENUE_PERIOD_CUTOFF = '2026-01'

const AGING_BAR_BUCKETS: { label: string; test: (aging: number) => boolean; color: string }[] = [
  { label: 'Current', test: aging => aging < 1, color: '#10b981' },
  { label: '1–30', test: aging => aging >= 1 && aging <= 30, color: '#84cc16' },
  { label: '31–60', test: aging => aging > 30 && aging <= 60, color: '#f59e0b' },
  { label: '61–90', test: aging => aging > 60 && aging <= 90, color: '#f97316' },
  { label: '91+', test: aging => aging > 90, color: '#ef4444' },
]

function parseAging(raw: string): number {
  const numeric = Number(String(raw).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(numeric) ? numeric : 0
}

function openBalance(row: ArAgingRow): number {
  const value = row['Open Balance']
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function apBalance(row: ApAgingRow): number {
  const value = row['AP Aging Balance']
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function fmtDays(days: number): string {
  return `${fmt.num(Math.round(days))}d`
}

type EntityStatus = 'healthy' | 'warning' | 'critical'

function entityStatus(dso: number, arRiskPct: number): EntityStatus {
  if (dso < 45 && arRiskPct < 15) return 'healthy'
  if (dso < 60 && arRiskPct < 25) return 'warning'
  return 'critical'
}

function statusDotColor(status: EntityStatus): string {
  if (status === 'healthy') return colors.status.green
  if (status === 'warning') return colors.status.yellow
  return colors.status.red
}

function statusLabel(status: EntityStatus): string {
  if (status === 'healthy') return 'Healthy'
  if (status === 'warning') return 'Warning'
  return 'Critical'
}

function statusBadgeClass(status: EntityStatus): string {
  if (status === 'healthy') return 'bg-emerald-50 text-emerald-800 ring-emerald-200'
  if (status === 'warning') return 'bg-amber-50 text-amber-800 ring-amber-200'
  return 'bg-red-50 text-red-800 ring-red-200'
}

function dsoColor(dso: number): string {
  if (dso < 45) return colors.status.green
  if (dso < 60) return colors.status.yellow
  return colors.status.red
}

interface EntityData {
  name: string
  dso: number
  collectionEfficiency: number
  arRiskPct: number
  arTotal: number
  apTotal: number
  quarterlyRevenue: number
  monthlyCashContribution: number
  status: EntityStatus
  agingBuckets: { label: string; amount: number; color: string }[]
  arPctOfTotal: number
}

type SortKey =
  | 'name'
  | 'arTotal'
  | 'apTotal'
  | 'dso'
  | 'collectionEfficiency'
  | 'arRiskPct'
  | 'monthlyCashContribution'
  | 'status'

type SortDir = 'asc' | 'desc'

function statusRank(status: EntityStatus): number {
  if (status === 'critical') return 2
  if (status === 'warning') return 1
  return 0
}

function computeEntities(
  arRecords: ArAgingRow[],
  apRecords: ApAgingRow[],
  revenueRecords: RevenueDetailRow[],
): EntityData[] {
  const arByEntity: Record<string, ArAgingRow[]> = {}
  for (const row of arRecords) {
    const business = row.Business
    if (!business) continue
    if (!arByEntity[business]) arByEntity[business] = []
    arByEntity[business].push(row)
  }

  const apByEntity: Record<string, number> = {}
  for (const row of apRecords) {
    const business = row.Company
    if (!business) continue
    apByEntity[business] = (apByEntity[business] || 0) + apBalance(row)
  }

  const revenueByEntity: Record<string, number> = {}
  for (const row of revenueRecords) {
    if (row.period >= REVENUE_PERIOD_CUTOFF) {
      revenueByEntity[row.entity] = (revenueByEntity[row.entity] || 0) + row.amount
    }
  }

  const portfolioArTotal = arRecords.reduce((sum, row) => sum + openBalance(row), 0)

  return Object.entries(arByEntity).map(([name, records]) => {
    const arTotal = records.reduce((sum, row) => sum + openBalance(row), 0)
    const weightedSum = records.reduce((sum, row) => {
      const aging = parseAging(row.Aging)
      return sum + openBalance(row) * aging
    }, 0)
    const dso = arTotal > 0 ? weightedSum / arTotal : 0

    const arUnder30 = records
      .filter(row => parseAging(row.Aging) <= 30)
      .reduce((sum, row) => sum + openBalance(row), 0)
    const collectionEfficiency = arTotal > 0 ? (arUnder30 / arTotal) * 100 : 0

    const arOver60 = records
      .filter(row => parseAging(row.Aging) > 60)
      .reduce((sum, row) => sum + openBalance(row), 0)
    const arRiskPct = arTotal > 0 ? (arOver60 / arTotal) * 100 : 0

    const apTotal = apByEntity[name] || 0
    const quarterlyRevenue = revenueByEntity[name] || 0
    const monthlyCashContribution = (quarterlyRevenue * 0.55) / 3
    const status = entityStatus(dso, arRiskPct)

    const agingBuckets = AGING_BAR_BUCKETS.map(bucket => ({
      label: bucket.label,
      color: bucket.color,
      amount: records
        .filter(row => bucket.test(parseAging(row.Aging)))
        .reduce((sum, row) => sum + openBalance(row), 0),
    }))

    return {
      name,
      dso,
      collectionEfficiency,
      arRiskPct,
      arTotal,
      apTotal,
      quarterlyRevenue,
      monthlyCashContribution,
      status,
      agingBuckets,
      arPctOfTotal: portfolioArTotal > 0 ? (arTotal / portfolioArTotal) * 100 : 0,
    }
  })
}

function KpiCard({
  label,
  value,
  subtext,
  color,
}: {
  label: string
  value: string
  subtext?: string
  color?: string
}) {
  return (
    <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-5 flex flex-col gap-1 min-w-0 dark:bg-dark-surface dark:border-dark-border">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {label}
      </span>
      <span
        className="text-2xl font-semibold truncate text-gray-900 dark:text-gray-100"
        style={color ? { color } : undefined}
      >
        {value}
      </span>
      {subtext && (
        <span className="text-xs text-gray-400 dark:text-gray-500 truncate">
          {subtext}
        </span>
      )}
    </div>
  )
}

interface ColDef {
  key: SortKey
  label: string
  align: 'left' | 'right'
  render: (entity: EntityData) => ReactNode
}

function buildColumns(): ColDef[] {
  return [
    {
      key: 'name',
      label: 'Entity',
      align: 'left',
      render: entity => (
        <span className="flex items-center gap-2 min-w-0">
          <span
            className="inline-block w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: statusDotColor(entity.status) }}
          />
          <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
            {entity.name}
          </span>
        </span>
      ),
    },
    {
      key: 'arTotal',
      label: 'AR Balance',
      align: 'right',
      render: entity => (
        <span className="font-mono text-gray-900 dark:text-gray-100">
          {fmt.compact(entity.arTotal)}
        </span>
      ),
    },
    {
      key: 'apTotal',
      label: 'AP Balance',
      align: 'right',
      render: entity => (
        <span className="font-mono text-gray-900 dark:text-gray-100">
          {fmt.compact(entity.apTotal)}
        </span>
      ),
    },
    {
      key: 'dso',
      label: 'DSO',
      align: 'right',
      render: entity => (
        <span className="font-mono font-semibold" style={{ color: dsoColor(entity.dso) }}>
          {fmtDays(entity.dso)}
        </span>
      ),
    },
    {
      key: 'collectionEfficiency',
      label: 'Collection Efficiency',
      align: 'right',
      render: entity => (
        <span className="font-mono text-gray-900 dark:text-gray-100">
          {fmt.pct(entity.collectionEfficiency)}
        </span>
      ),
    },
    {
      key: 'arRiskPct',
      label: 'AR Risk %',
      align: 'right',
      render: entity => (
        <span
          className="font-mono"
          style={{
            color:
              entity.arRiskPct > 25
                ? colors.status.red
                : entity.arRiskPct > 15
                  ? colors.status.yellow
                  : colors.status.green,
          }}
        >
          {fmt.pct(entity.arRiskPct)}
        </span>
      ),
    },
    {
      key: 'monthlyCashContribution',
      label: 'Monthly Cash Contribution',
      align: 'right',
      render: entity => (
        <span className="font-mono text-gray-900 dark:text-gray-100">
          {fmt.compact(entity.monthlyCashContribution)}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      align: 'left',
      render: entity => (
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${statusBadgeClass(
            entity.status,
          )}`}
        >
          {statusLabel(entity.status)}
        </span>
      ),
    },
  ]
}

const columns = buildColumns()

function MetricTile({
  label,
  value,
  color,
}: {
  label: string
  value: string
  color?: string
}) {
  return (
    <div className="rounded-lg bg-white/80 dark:bg-dark-surface p-3 border border-gray-200/80 dark:border-dark-border shadow-sm">
      <div className="text-gray-500 dark:text-gray-400 text-[10px] uppercase tracking-wide mb-0.5">
        {label}
      </div>
      <div
        className="font-mono font-semibold text-base text-gray-900 dark:text-gray-100"
        style={color ? { color } : undefined}
      >
        {value}
      </div>
    </div>
  )
}

function ExpandedDetail({ entity }: { entity: EntityData }) {
  const bucketTotal = entity.agingBuckets.reduce((sum, bucket) => sum + bucket.amount, 0)
  const portfolioBarColor = statusDotColor(entity.status)

  return (
    <div className="bg-gray-50 dark:bg-dark-surface-2 px-6 py-5 space-y-5 border-t border-gray-100 dark:border-dark-border">
      <div>
        <div className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide mb-2">
          AR aging breakdown
        </div>
        <div className="flex h-7 rounded-md overflow-hidden ring-1 ring-gray-200/80 dark:ring-dark-border">
          {entity.agingBuckets.map(bucket => {
            const pct = bucketTotal > 0 ? (bucket.amount / bucketTotal) * 100 : 0
            if (pct < 0.5) return null
            return (
              <div
                key={bucket.label}
                className="relative group flex items-center justify-center text-[10px] font-mono font-semibold text-white transition-all min-w-0"
                style={{ width: `${pct}%`, backgroundColor: bucket.color }}
                title={`${bucket.label}: ${fmt.currency(bucket.amount)} (${fmt.pct(pct)})`}
              >
                {pct >= 10 && (
                  <span className="truncate px-1">
                    {bucket.label}: {fmt.compact(bucket.amount)}
                  </span>
                )}
              </div>
            )
          })}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
          {entity.agingBuckets.map(bucket => (
            <div
              key={bucket.label}
              className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400"
            >
              <span
                className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                style={{ backgroundColor: bucket.color }}
              />
              {bucket.label}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <MetricTile label="DSO" value={fmtDays(entity.dso)} color={dsoColor(entity.dso)} />
        <MetricTile
          label="Collection efficiency"
          value={fmt.pct(entity.collectionEfficiency)}
        />
        <MetricTile
          label="AR risk"
          value={fmt.pct(entity.arRiskPct)}
          color={
            entity.arRiskPct > 25
              ? colors.status.red
              : entity.arRiskPct > 15
                ? colors.status.yellow
                : colors.status.green
          }
        />
        <MetricTile label="AR total" value={fmt.currency(entity.arTotal)} />
        <MetricTile label="AP total" value={fmt.currency(entity.apTotal)} />
        <MetricTile label="Q revenue" value={fmt.currency(entity.quarterlyRevenue)} />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <span className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide whitespace-nowrap">
          Portfolio AR %
        </span>
        <div className="flex-1 h-2 bg-gray-200 dark:bg-dark-border rounded-full overflow-hidden min-w-[120px]">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${Math.min(entity.arPctOfTotal, 100)}%`,
              backgroundColor: portfolioBarColor,
            }}
          />
        </div>
        <span className="font-mono text-xs text-gray-900 dark:text-gray-100 sm:text-right">
          {fmt.pct(entity.arPctOfTotal)}
        </span>
      </div>
    </div>
  )
}

function EntityRow({
  entity,
  isExpanded,
  onToggle,
}: {
  entity: EntityData
  isExpanded: boolean
  onToggle: () => void
}) {
  return (
    <>
      <tr
        className="border-b border-gray-100 dark:border-dark-border hover:bg-gray-50/80 dark:hover:bg-dark-surface-2/40 cursor-pointer transition-colors"
        onClick={onToggle}
      >
        {columns.map(column => (
          <td
            key={column.key}
            className={`px-4 py-3 whitespace-nowrap ${
              column.align === 'right' ? 'text-right' : 'text-left'
            }`}
          >
            {column.render(entity)}
          </td>
        ))}
      </tr>
      {isExpanded && (
        <tr className="border-b border-gray-100 dark:border-dark-border">
          <td colSpan={columns.length} className="p-0">
            <ExpandedDetail entity={entity} />
          </td>
        </tr>
      )}
    </>
  )
}

export default function EntityScorecardPage() {
  const arQuery = useArAging()
  const apQuery = useApAging()
  const revenueQuery = useRevenueDetail()

  const arRecords = useMemo(() => arQuery.data ?? [], [arQuery.data])
  const apRecords = useMemo(() => apQuery.data ?? [], [apQuery.data])
  const revenueRecords = useMemo(
    () => revenueQuery.data ?? [],
    [revenueQuery.data],
  )

  const entities = useMemo(
    () => computeEntities(arRecords, apRecords, revenueRecords),
    [apRecords, arRecords, revenueRecords],
  )

  const [sortKey, setSortKey] = useState<SortKey>('status')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [expandedRow, setExpandedRow] = useState<string | null>(null)

  const sorted = useMemo(() => {
    const copy = [...entities]
    copy.sort((a, b) => {
      let comparison = 0
      if (sortKey === 'name') {
        comparison = a.name.localeCompare(b.name)
      } else if (sortKey === 'status') {
        comparison = statusRank(a.status) - statusRank(b.status)
        if (comparison === 0) comparison = a.dso - b.dso
      } else {
        comparison = (a[sortKey] as number) - (b[sortKey] as number)
      }
      return sortDir === 'desc' ? -comparison : comparison
    })
    return copy
  }, [entities, sortDir, sortKey])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(direction => (direction === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const totalAr = entities.reduce((sum, entity) => sum + entity.arTotal, 0)
  const totalAp = entities.reduce((sum, entity) => sum + entity.apTotal, 0)
  const totalApAll = useMemo(
    () => apRecords.reduce((sum, row) => sum + apBalance(row), 0),
    [apRecords],
  )

  const portfolioDso =
    totalAr > 0
      ? entities.reduce((sum, entity) => sum + entity.dso * entity.arTotal, 0) / totalAr
      : 0

  const portfolioCollectionEfficiency =
    totalAr > 0
      ? entities.reduce(
          (sum, entity) => sum + entity.collectionEfficiency * entity.arTotal,
          0,
        ) / totalAr
      : 0

  const portfolioArOver60 = arRecords
    .filter(row => parseAging(row.Aging) > 60)
    .reduce((sum, row) => sum + openBalance(row), 0)
  const portfolioRiskScore = totalAr > 0 ? (portfolioArOver60 / totalAr) * 100 : 0

  const loading = arQuery.isLoading || apQuery.isLoading || revenueQuery.isLoading
  const hasError = arQuery.error || apQuery.error || revenueQuery.error

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight">
          Entity Scorecard
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-3xl">
          AR-weighted working capital view by entity: DSO, collection quality,
          overdue risk, and implied monthly cash contribution from recognized
          revenue ({REVENUE_PERIOD_CUTOFF} onward).
        </p>
      </header>

      {hasError && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm px-4 py-3">
          Unable to load scorecard data. Please try again.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard label="Total entities" value={loading ? '…' : String(entities.length)} />
        <KpiCard label="Total AR balance" value={loading ? '…' : fmt.compact(totalAr)} />
        <KpiCard
          label="Total AP balance"
          value={loading ? '…' : fmt.compact(totalApAll)}
          subtext={entities.length ? `Mapped to entities: ${fmt.compact(totalAp)}` : undefined}
        />
        <KpiCard
          label="Avg DSO"
          value={loading ? '…' : fmtDays(portfolioDso)}
          color={dsoColor(portfolioDso)}
        />
        <KpiCard
          label="Avg collection efficiency"
          value={loading ? '…' : fmt.pct(portfolioCollectionEfficiency)}
        />
        <KpiCard
          label="Portfolio risk score"
          value={loading ? '…' : fmt.pct(portfolioRiskScore)}
          subtext="Share of AR over 60 days"
          color={
            portfolioRiskScore > 25
              ? colors.status.red
              : portfolioRiskScore > 15
                ? colors.status.yellow
                : colors.status.green
          }
        />
      </div>

      <div className="rounded-xl bg-white shadow-sm border border-gray-100 dark:bg-dark-surface dark:border-dark-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[960px]">
            <thead>
              <tr className="border-b border-gray-100 dark:border-dark-border bg-gray-50/80 dark:bg-dark-surface-2">
                {columns.map(column => (
                  <th
                    key={column.key}
                    className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer select-none hover:text-gray-800 dark:hover:text-gray-200 transition-colors whitespace-nowrap ${
                      column.align === 'right' ? 'text-right' : 'text-left'
                    }`}
                    onClick={() => handleSort(column.key)}
                  >
                    <span className="inline-flex items-center gap-1">
                      {column.label}
                      {sortKey === column.key ? (
                        sortDir === 'desc' ? (
                          <ChevronDown className="w-3 h-3" />
                        ) : (
                          <ChevronUp className="w-3 h-3" />
                        )
                      ) : (
                        <ChevronsUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    Loading entities...
                  </td>
                </tr>
              )}
              {!loading &&
                sorted.map(entity => (
                  <EntityRow
                    key={entity.name}
                    entity={entity}
                    isExpanded={expandedRow === entity.name}
                    onToggle={() =>
                      setExpandedRow(previous =>
                        previous === entity.name ? null : entity.name,
                      )
                    }
                  />
                ))}
              {!loading && sorted.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No AR aging rows found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
